import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, BookOpenText, Trophy, Zap, Swords, MapPin, ChevronRight,
} from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2N PR 5 — Rivalry Documentary (Living Mythology page).
 *
 * Route: /arenax/rivalries/:squadA/:squadB/documentary
 *
 * Renders the documentary opener + episodic timeline. Cinematic, editorial,
 * never analytics-speak. Auto-evolves as new meetings complete (backend
 * rebuilds on /api/arenax/rivalries/{a}/{b}/documentary).
 */
const KIND_META = {
  episode_final:        { Icon: Trophy, label: 'FINAL',        glow: 'from-amber-500/20 to-rose-500/10  border-amber-500/40' },
  episode_comeback:     { Icon: Zap,    label: 'COMEBACK',     glow: 'from-purple-500/15 to-amber-500/10 border-purple-500/35' },
  episode_clip_moment:  { Icon: Trophy, label: 'MEMORABLE MOMENT', glow: 'from-rose-500/20 to-purple-500/15 border-rose-500/40' },
  episode_meeting:      { Icon: Swords, label: 'MEETING',      glow: 'from-slate-800/40 to-slate-900/0  border-slate-700' },
};

export default function ArenaxRivalryDocumentary() {
  const { squadA, squadB } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState(null);

  useEffect(() => {
    if (!squadA || !squadB || squadA === squadB) return;
    setLoading(true);
    api.get(`/arenax/rivalries/${squadA}/${squadB}/documentary`)
      .then(r => {
        setData(r.data?.documentary || null);
        setReason(r.data?.reason || null);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [squadA, squadB]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 grid place-items-center">
        <div className="text-sm text-slate-500">Loading documentary…</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header onBack={() => navigate(-1)} />
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <BookOpenText className="h-8 w-8 text-slate-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-100 mb-2">Documentary not unlocked yet</h1>
          <p className="text-slate-400 leading-relaxed">
            A documentary unlocks once two crews share enough history to tell a real story.
          </p>
          {reason && (
            <p className="mt-3 text-xs text-slate-600 italic">{reason}</p>
          )}
        </div>
      </div>
    );
  }

  const header = data.header || {};

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="arenax-rivalry-documentary">
      <Header onBack={() => navigate(-1)} />

      {/* Documentary opener */}
      <section className="relative overflow-hidden border-b border-slate-800">
        <div className="absolute inset-0 pointer-events-none opacity-20"
          style={{
            background: 'radial-gradient(ellipse at 30% 30%, rgba(244,63,94,0.5), transparent 60%), radial-gradient(ellipse at 70% 70%, rgba(124,58,237,0.4), transparent 60%)',
          }} />
        <div className="relative max-w-3xl mx-auto px-6 pt-12 pb-10">
          <div className="flex items-center gap-2 text-rose-300 mb-3">
            <BookOpenText className="h-4 w-4" />
            <span className="text-[10px] tracking-[0.35em] font-black uppercase">Living Documentary</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-4 leading-[1.05]"
            data-testid="arenax-doc-title">
            {header.title || 'The Rivalry'}
          </h1>
          <div className="text-lg text-slate-300 leading-relaxed font-medium italic mb-6"
            data-testid="arenax-doc-tagline">
            {header.tagline}
          </div>
          <div className="text-base text-slate-200 leading-relaxed"
            data-testid="arenax-doc-narrative">
            {header.narrative}
          </div>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Meetings"   value={data.total_meetings} />
            <Stat label="Finals"     value={data.finals_count} accent="amber" />
            <Stat label="Comebacks"  value={data.comebacks_count} accent="purple" />
            <Stat label="Heartbreaks" value={data.heartbreaks_count} accent="rose" />
          </div>
        </div>
      </section>

      {/* Episodic timeline */}
      <section className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 mb-6">
          <span className="text-[10px] tracking-[0.35em] font-black text-cyan-300 uppercase">Episodes</span>
          <div className="h-px flex-1 bg-slate-800" />
          <span className="text-xs text-slate-500">{data.episodes?.length || 0} so far</span>
        </div>
        <div className="space-y-3">
          {(data.episodes || []).map((ep, i) => (
            <EpisodeCard key={ep.match_id || i} episode={ep} index={(data.episodes.length - i)} />
          ))}
        </div>
      </section>
    </div>
  );
}

function Header({ onBack }) {
  return (
    <div className="sticky top-0 z-20 bg-slate-950/85 backdrop-blur border-b border-slate-800">
      <div className="max-w-3xl mx-auto px-6 py-3 flex items-center gap-3">
        <button onClick={onBack}
          data-testid="arenax-doc-back"
          className="p-2 rounded-lg hover:bg-slate-900 transition-colors">
          <ArrowLeft className="h-4 w-4 text-slate-300" />
        </button>
        <div className="text-sm text-slate-400 font-medium">Documentary</div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = 'cyan' }) {
  const tone = {
    cyan:   'text-cyan-300 border-cyan-500/30',
    amber:  'text-amber-300 border-amber-500/30',
    purple: 'text-purple-300 border-purple-500/30',
    rose:   'text-rose-300 border-rose-500/30',
  }[accent];
  return (
    <div className={`rounded-xl border ${tone} px-3 py-3 bg-slate-900/40`}>
      <div className="text-2xl font-black">{value ?? 0}</div>
      <div className="text-[10px] tracking-[0.25em] text-slate-400 uppercase mt-1">{label}</div>
    </div>
  );
}

function EpisodeCard({ episode, index }) {
  const meta = KIND_META[episode.kind] || KIND_META.episode_meeting;
  const Icon = meta.Icon;
  const content = (
    <div className={`relative rounded-2xl border bg-gradient-to-r ${meta.glow} px-5 py-4 hover:translate-x-1 transition-transform`}
      data-testid={`arenax-doc-episode-${episode.kind}`}>
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-12 h-12 rounded-xl bg-slate-900/60 border border-slate-700 grid place-items-center">
          <Icon className="h-5 w-5 text-slate-200" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1.5">
            <span className="text-[10px] tracking-[0.25em] font-black text-slate-400 uppercase">{meta.label}</span>
            <span className="text-[10px] text-slate-500 font-mono">EP {index}</span>
            {episode.venue_name && (
              <span className="inline-flex items-center gap-1 text-[10px] text-cyan-300">
                <MapPin className="h-3 w-3" /> {episode.venue_name}
              </span>
            )}
          </div>
          <div className="text-[15px] text-slate-100 leading-snug">{episode.narrative}</div>
        </div>
        {episode.tournament_id && <ChevronRight className="h-4 w-4 text-slate-500 mt-1" />}
      </div>
    </div>
  );
  if (episode.tournament_id) {
    return <Link to={`/arenax/tournaments/${episode.tournament_id}`} className="block">{content}</Link>;
  }
  return content;
}
